import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  ApartmentOutlined,
  DevicesOutlined,
  ErrorOutline,
  PersonOutline,
  ReportProblemOutlined,
  SettingsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { theme } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { Loader } from "components/Loader";
import { FilterProvider } from "pages/FilterContext";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import {
  checkViewerPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";
import { canUseCompanyContext } from "services/utils/companyAccess";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { useFetch } from "services/hooks/useFetch";
import { PunchProcessingExceptionsHeader } from "./headers";
import {
  countByCategory,
  issueTone as issueToneKey,
  mapPunchExceptionResponse,
  punchExceptionsUrl,
  unappliedFilters,
} from "./punchExceptionData";

export const PUNCH_EXCEPTIONS_REPORT_NAME = "Punch Processing Exceptions";

const ALL_DEPARTMENTS = { id: null, label: "All Departments" };
const ALL_STAGES = { value: null, label: "All Stages" };
const ALL_ISSUES = { value: null, label: "All Issues" };

// Card keys match countByCategory (grouped by the API's issue_category).
const SUMMARY_CARDS = [
  {
    key: "total",
    label: "Total Exceptions",
    icon: ReportProblemOutlined,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "employee",
    label: "Unknown Employee",
    icon: PersonOutline,
    accent: "#1D4ED8",
    tint: "#EEF4FF",
  },
  {
    key: "device",
    label: "Device Issues",
    icon: DevicesOutlined,
    accent: "#0F766E",
    tint: "#E6F6F4",
  },
  {
    key: "processing",
    label: "Processing Failures",
    icon: SettingsOutlined,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
];

const TONE_COLORS = {
  warning: { color: "#B45309", background: "#FFF4E5" },
  error: { color: "#DC2626", background: "#FEECEC" },
  info: { color: "#1D4ED8", background: "#EAF1FF" },
};
const DIRECTION_COLORS = {
  IN: { color: "#15803D", background: "#E8F7EE" },
  OUT: { color: "#DC2626", background: "#FEECEC" },
};
const issueTone = (issueType) => TONE_COLORS[issueToneKey(issueType)];

const Pill = ({ label, color, background, minWidth }) => (
  <Mui.Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      px: 1.25,
      height: 24,
      minWidth,
      borderRadius: "6px",
      fontSize: 12.5,
      fontWeight: 600,
      color,
      backgroundColor: background,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Mui.Box>
);

const TwoLineCell = ({ primary, secondary }) => (
  <Mui.Box sx={{ minWidth: 0 }}>
    <Mui.Typography
      noWrap
      sx={{ fontSize: 14, fontWeight: 500, color: designColors.slate800 }}
    >
      {primary}
    </Mui.Typography>
    <Mui.Typography
      noWrap
      sx={{ fontSize: 12.5, color: designColors.slate500 }}
    >
      {secondary}
    </Mui.Typography>
  </Mui.Box>
);

// Filter row is a grid: from md up the date range keeps its natural width
// and every dropdown gets an equal column (minmax(0, 1fr)), so they always
// stay on one row and long selections ("Issue Type - Attendance processing
// failed (5)") end in an ellipsis instead of wrapping. Below md each filter
// takes a full-width line.
const filterRowSx = {
  display: "grid",
  gap: 1.5,
  alignItems: "center",
  width: "100%",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "auto" },
  gridAutoFlow: { xs: "row", md: "column" },
  gridAutoColumns: { md: "minmax(0, 1fr)" },
};
const filterButtonSx = { width: "100%", minWidth: 0 };

export const PunchProcessingExceptionsReport = ({
  setReportHeaderActions,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
}) => {
  const {
    domain,
    token,
    userDetails,
    userScreenAccess,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [rawRows, setRawRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [department, setDepartment] = useState(ALL_DEPARTMENTS);
  const [stage, setStage] = useState(ALL_STAGES);
  const [issueType, setIssueType] = useState(ALL_ISSUES);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  const reportsModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );
  const subModuleId = reportsModule?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, PUNCH_EXCEPTIONS_REPORT_NAME),
  )?.sub_module_id;
  // Same rule as every other report: the role needs viewer access on this
  // submodule under "Reports" (the Reports landing page already hides the
  // card without it).
  const checkAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    PUNCH_EXCEPTIONS_REPORT_NAME,
  );

  const handleSavedViewChange = useCallback(() => setPage(0), []);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "punch_processing_exceptions_saved_views",
    fullHeaders: PunchProcessingExceptionsHeader,
    screenAccess: checkAccess,
    onViewChange: handleSavedViewChange,
  });

  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  const departmentOptions = useMemo(
    () => [
      ALL_DEPARTMENTS,
      ...(Array.isArray(departmentData) ? departmentData : []).map((item) => ({
        id: item.id,
        label: item.department_name,
      })),
    ],
    [departmentData],
  );
  // Issue types come from the API's counts.issue_types; only the ones with
  // records in this range are listed.
  const issueOptions = useMemo(
    () => [
      ALL_ISSUES,
      ...issueTypes
        .filter((item) => Number(item.count) > 0)
        .map((item) => ({
          value: item.value,
          label: `${item.label} (${item.count})`,
        })),
    ],
    [issueTypes],
  );
  // Stages: the values present in the loaded rows.
  const stageOptions = useMemo(
    () => [
      ALL_STAGES,
      ...[...new Set(rawRows.map((row) => row.processing_stage))]
        .filter((value) => value && value !== "-")
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    ],
    [rawRows],
  );

  // Only date range, company and department go to the API; it returns
  // every row (no paging), so Issue Type / Stage filter on screen below.
  useEffect(() => {
    let cancelled = false;
    const clearResults = () => {
      setRawRows([]);
      setIssueTypes([]);
    };
    const fetchExceptions = async () => {
      setIsLoading(true);
      try {
        if (!canUseCompanyContext(topbarCompanyId, userDetails?.type)) {
          clearResults();
          return;
        }
        const response = await fetch(
          punchExceptionsUrl(domain, {
            event_time_from: startDate,
            event_time_to: endDate,
            company_id: topbarCompanyId,
            department_id: department.id,
          }),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.status === 409) {
          clearResults();
          sessionHandling();
          return;
        }
        if (!response.ok) {
          throw new Error(
            res?.errors?.map((item) => item.message).join(" ") ||
              res?.detail ||
              res?.error ||
              "Failed to load punch processing exceptions",
          );
        }
        const result = mapPunchExceptionResponse(res);
        // If the server didn't echo department_id back in `query`, it
        // ignored it, so filter the rows here instead.
        const departmentIgnored =
          unappliedFilters(
            { department_id: department.id },
            result.acceptedParams,
          ).length > 0;
        if (departmentIgnored) {
          console.warn(
            "[PunchProcessingExceptions] department_id not accepted by the API; filtering on screen.",
          );
        }
        setRawRows(
          departmentIgnored
            ? result.rows.filter((row) => row.department_id === department.id)
            : result.rows,
        );
        setIssueTypes(result.issueTypes);
      } catch (error) {
        if (!cancelled) {
          clearResults();
          showToast("error", error.message);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchExceptions();
    return () => {
      cancelled = true;
    };
  }, [
    department.id,
    domain,
    endDate,
    startDate,
    token,
    topbarCompanyId,
    userDetails?.type,
  ]);

  // Issue Type / Stage dropdowns first; CustomFilter's search, sort and
  // filters then narrow this list and report back below.
  const rows = useMemo(
    () =>
      rawRows.filter(
        (row) =>
          (!issueType.value || row.issue_type === issueType.value) &&
          (!stage.value || row.processing_stage === stage.value),
      ),
    [rawRows, issueType.value, stage.value],
  );

  const handleFilteredDataChange = useCallback((data) => {
    setFilteredRows(data);
    setPage(0);
  }, []);

  const counts = useMemo(() => countByCategory(filteredRows), [filteredRows]);
  const pagedRows = filteredRows.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage,
  );

  const handleDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStart = moment(rangeStart);
    const parsedEnd = moment(rangeEnd);
    if (!parsedStart.isValid() || !parsedEnd.isValid()) return;
    setStartDate(parsedStart.format("YYYY-MM-DD"));
    setEndDate(parsedEnd.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const getReportMetadata = useCallback(
    () => ({
      "Company Name": topbarCompanyName || "All",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      Department: department.label,
      "Processing Stage": stage.label,
      "Issue Type": issueType.label,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      "Overall Total Count": filteredRows.length,
    }),
    [
      department.label,
      endDate,
      filteredRows.length,
      issueType.label,
      stage.label,
      startDate,
      topbarCompanyName,
    ],
  );

  // Filters render inline below the title, same as Device Status.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const renderCell = (header, row) => {
    switch (header.key) {
      case "employee_label":
        return (
          <Mui.Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Mui.Avatar
              sx={{ width: 30, height: 30, fontSize: 13 }}
              alt={row.employee_name}
              src={row.image_url}
            >
              {row.is_unknown_employee ? (
                <PersonOutline fontSize="small" />
              ) : null}
            </Mui.Avatar>
            <TwoLineCell
              primary={row.employee_name}
              secondary={row.employee_code}
            />
          </Mui.Stack>
        );
      case "direction":
        return (
          <Pill
            label={row.direction}
            minWidth={46}
            {...(DIRECTION_COLORS[row.direction] || DIRECTION_COLORS.IN)}
          />
        );
      case "device_label":
        return (
          <TwoLineCell
            primary={row.device_name}
            secondary={row.device_code ? `(${row.device_code})` : row.source}
          />
        );
      case "issue":
        // issue_detail (e.g. the processing error) shows on hover.
        return (
          <Mui.Tooltip
            title={row.issue_detail}
            arrow
            slotProps={tooltipStyleProps}
            disableHoverListener={!row.issue_detail}
          >
            <span>
              <Pill label={row.issue} {...issueTone(row.issue_type)} />
            </span>
          </Mui.Tooltip>
        );
      default:
        return row?.[header.key] || "-";
    }
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            <Mui.Box sx={filterRowSx}>
              <Mui.Box sx={{ minWidth: 0 }}>
                <ApprovalDateRangeFilter
                  thisMonthToDate
                  startDate={startDate}
                  endDate={endDate}
                  dateRangeLabel={dateRangeLabel}
                  onChange={handleDateRange}
                  showToday={true}
                  includeCurrentDate={true}
                  allowFutureDate={false}
                  sx={{
                    minHeight: 40,
                    height: 40,
                    px: 2,
                    width: { xs: "100%", md: "auto" },
                    backgroundColor: designColors.slate100,
                    border: "none",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: designColors.slate100,
                      border: "none",
                      boxShadow: "none",
                    },
                  }}
                />
              </Mui.Box>
              {embeddedReportTypes?.length ? (
                <CompactHeaderSelectFilter
                  dataList={embeddedReportTypes}
                  selectedValue={selectedReportTypeOption}
                  updateDataFunction={onReportTypeChange}
                  Icons={PersonOutline}
                  staticLabel="Report Type"
                  widthSx={filterButtonSx}
                />
              ) : null}
              <CompactHeaderSelectFilter
                dataList={departmentOptions}
                selectedValue={department}
                updateDataFunction={(event, option) => {
                  setDepartment(option?.id ? option : ALL_DEPARTMENTS);
                  setPage(0);
                }}
                Icons={ApartmentOutlined}
                staticLabel="Department"
                widthSx={filterButtonSx}
              />
              <CompactHeaderSelectFilter
                dataList={stageOptions}
                selectedValue={stage}
                updateDataFunction={(event, option) => {
                  setStage(option?.value ? option : ALL_STAGES);
                  setPage(0);
                }}
                Icons={TuneOutlined}
                staticLabel="Processing Stage"
                widthSx={filterButtonSx}
              />
              <CompactHeaderSelectFilter
                dataList={issueOptions}
                selectedValue={issueType}
                updateDataFunction={(event, option) => {
                  setIssueType(option?.value ? option : ALL_ISSUES);
                  setPage(0);
                }}
                Icons={ErrorOutline}
                staticLabel="Issue Type"
                widthSx={filterButtonSx}
              />
            </Mui.Box>
          </Mui.Box>

          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              "@media (max-width:599.95px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {SUMMARY_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.label}
                value={counts[card.key] ?? 0}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Punch Processing Exceptions"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={rows}
              filteredData={filteredRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={checkAccess}
              showtitle_count={true}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              labeledToolbar={true}
              searchPlaceholder="Search by employee name, code, device, issue or event ID..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                  borderLeft: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  borderBottom: `1px solid ${designColors.slate200}`,
                }}
              >
                <ApproveTable
                  stickyHeader
                  sx={{ minWidth: 1100, width: "100%" }}
                >
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header) => (
                        <Mui.TableCell
                          key={header.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {filteredRows.length === 0 ? (
                      <TableEmptyState
                        dataLength={rows}
                        colSpan={visibleHeaders.length || 1}
                      />
                    ) : (
                      // Paged on screen: the API returns every row.
                      pagedRows.map((row) => (
                        <Mui.TableRow
                          key={row.id}
                          hover
                          sx={{
                            ...designShell.tableRowHover,
                            "&:last-child td": { borderBottom: "none" },
                            "& td": { backgroundColor: designColors.white },
                          }}
                        >
                          {visibleHeaders.map((header) => (
                            <Mui.TableCell
                              key={header.key}
                              sx={approveTableBodyCell}
                            >
                              {renderCell(header, row)}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: filteredRows.length,
                itemLabel: "exceptions",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
